import { v4 as uuid } from "uuid";
import { AuthoringBlockType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class ContactInfoCategory extends BaseCategory {
    get name() {
        return "Contact Info";
    }

    get type() {
        return SlideCategories.CONTACT_INFO;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for displaying contact information including company name, address, phone, email, website, and social media handles. Can optionally include a map or image.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a contact information slide based on the user's prompt.
        Extract contact details such as company/organization name, address, phone, email, website, and social media.
        Organize the information into appropriate contact items.`;
    }

    getPromptSchema() {
        return {
            contactHeader: "main header (company/organization name)",
            contactInfo: [{
                type: "address | phone | email | fax | website | social | text",
                text: "the contact information text (phone number, email, etc.)",
                socialType: "optional: if type is 'social', specify the platform (e.g., 'twitter', 'linkedin', 'facebook')"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getImagePrompt() {
        return false;
    }

    getSlideModels(model, options = {}) {
        let models = [];

        // Build variants based on available data
        if (model.contactInfo?.find(item => item.type === "address") != null) {
            // If we have an address, create map variants
            models.push(this.buildContactInfoSlide(model, "left_map", options));
            // models.push(this.buildContactInfoSlide(model, "right_map"));
        }

        // If we have an image search, create image variants
        models.push(this.buildContactInfoSlide(model, "left_picture", options));
        // models.push(this.buildContactInfoSlide(model, "right_picture"));

        // Always add a text-only variant
        models.push(this.buildContactInfoSlide(model, "none", options));

        return models;
    }

    buildContactInfoSlide(model, layout = "none", options = {}) {
        let builder = new SlideModelBuilder("contactus", model);

        const items = [];

        let address = model.contactInfo?.find(item => item.type === "address");

        // Add contact header if provided
        items.push({
            type: "contact_header",
            text: {
                blocks: [
                    builder.createBlock(model.contactHeader || model.slideTitle, TextStyleType.TITLE)
                ]
            }
        });

        // Add address if provided and layout uses map
        if (address) {
            items.push({ type: "address" });
        }

        // Add contact items
        if (model.contactInfo && model.contactInfo.length > 0) {
            for (const contactItem of model.contactInfo) {
                if (contactItem.type === "address") {
                    continue;
                }

                const item = {
                    type: contactItem.type || "text",
                    text: {
                        blocks: [
                            {
                                html: contactItem.text || "",
                                textStyle: TextStyleType.BODY,
                                type: AuthoringBlockType.TEXT
                            }
                        ]
                    }
                };

                // Add social type if specified
                if (contactItem.type === "social" && contactItem.socialType) {
                    item.social_type = contactItem.socialType;
                }

                items.push(item);
            }
        }

        // Create primary data
        const primaryData = {
            layout: layout,
            items: items,
            map_address: address
        };

        // Add image if we have image search and using picture layout
        if (model.slideImage && (layout === "left_picture" || layout === "right_picture" || layout === "top_picture")) {
            Object.assign(primaryData, builder.createImage(model.slideImage.prompt, {}, true));
        }

        builder.createPrimary(primaryData);

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
